import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { confirmDialog } from "primereact/confirmdialog";
import { InputSwitch } from "primereact/inputswitch";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";

import { IPayerOption, payerCountLabel } from "../helpers/ruleBucket";
import { IRuleBucket } from "../hooks/useRuleBucket";

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const Hint = styled.span`
  color: #6b7280;
  font-size: 12px;
  flex-basis: 100%;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const PayerChip = styled(Chip)`
  &.p-chip {
    padding-right: 0.25rem;
  }
  .p-button.p-button-icon-only {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

const LAST_PAYER_TOOLTIP = "Use Delete to remove the rule entirely";
const LAST_PAYER_TOOLTIP_TARGET = "bucket-last-payer-remove";

/** The switch's label; while it is on, N counts the payers the save will leave the rule on. */
export const applyToAllLabel = (bucket: IRuleBucket) =>
  `Apply changes to all ${payerCountLabel(
    bucket.applyToAll ? bucket.activePayers.length : bucket.members.length
  )}`;

/** The "Apply changes to all N payers" switch shown at the top of the provider/payer section. */
export function RuleBucketSwitch({ bucket }: { bucket: IRuleBucket }) {
  return (
    <SwitchRow>
      <InputSwitch
        inputId="applyToAllPayers"
        checked={bucket.applyToAll}
        onChange={(e) => bucket.setApplyToAll(!!e.value)}
      />
      <label
        htmlFor="applyToAllPayers"
        style={{ fontWeight: 600, cursor: "pointer" }}
      >
        {applyToAllLabel(bucket)}
      </label>
      {!bucket.applyToAll && (
        <Hint>
          Only this payer&apos;s copy will change; it will no longer be grouped
          with the others.
        </Hint>
      )}
    </SwitchRow>
  );
}

/**
 * The bucket's payers as removable chips, plus an autocomplete to add more. Removing a member
 * only marks its row for deletion — nothing changes until the form is saved.
 */
export function RuleBucketPayersField({ bucket }: { bucket: IRuleBucket }) {
  const { data: allPayers } = useGetAllPayersQuery();
  const [query, setQuery] = useState<string | IPayerOption>("");
  const [suggestions, setSuggestions] = useState<IPayerOption[]>([]);

  const addablePayers = useMemo(() => {
    const taken = new Set(bucket.activePayers.map((payer) => payer.value));
    return (allPayers?.data ?? [])
      .map((payer: any) => ({ name: payer.name, value: payer.id }))
      .filter((payer: IPayerOption) => !taken.has(payer.value));
  }, [allPayers, bucket.activePayers]);

  const searchPayer = (event: { query: string }) => {
    const text = event.query.trim().toLowerCase();
    setSuggestions(
      text
        ? addablePayers.filter((payer: IPayerOption) =>
            payer.name.toLowerCase().startsWith(text)
          )
        : [...addablePayers]
    );
  };

  const onlyOneMemberLeft = bucket.activeMembers.length <= 1;

  const confirmRemoveMember = (id: string, payerName: string) => {
    confirmDialog({
      header: "Remove payer",
      icon: "pi pi-exclamation-triangle",
      message: `Remove ${payerName} from this rule? Its row, attachments and history will be deleted when you save.`,
      accept: () => bucket.removeMember(id),
    });
  };

  const removeButton = (
    label: string,
    onClick: () => void,
    disabled = false
  ) => {
    const button = (
      <Button
        type="button"
        icon="pi pi-times-circle"
        className="p-button-rounded p-button-text p-button-secondary p-button-sm"
        aria-label={`Remove ${label}`}
        disabled={disabled}
        onClick={onClick}
      />
    );
    if (!disabled) return button;
    // A disabled button gets no pointer events, so the tooltip hangs off a wrapper React owns.
    return (
      <span
        className={LAST_PAYER_TOOLTIP_TARGET}
        data-pr-tooltip={LAST_PAYER_TOOLTIP}
        data-pr-position="top"
      >
        {button}
      </span>
    );
  };

  return (
    <>
      <Label htmlFor="addBucketPayer">Payers</Label>
      <Tooltip target={`.${LAST_PAYER_TOOLTIP_TARGET}`} />
      <ChipList data-testid="bucket-payer-chips">
        {bucket.activeMembers.map((member) => (
          <PayerChip
            key={member.id}
            template={
              <>
                <span className="p-chip-text">{member.payerName}</span>
                {removeButton(
                  member.payerName,
                  () => confirmRemoveMember(member.id, member.payerName),
                  onlyOneMemberLeft
                )}
              </>
            }
          />
        ))}
        {bucket.addedPayers.map((payer) => (
          <PayerChip
            key={`added-${payer.value}`}
            template={
              <>
                <span className="p-chip-text">{`${payer.name} (new)`}</span>
                {removeButton(payer.name, () =>
                  bucket.removeAddedPayer(payer.value)
                )}
              </>
            }
          />
        ))}
      </ChipList>
      <AutoComplete
        dropdown
        field="name"
        inputId="addBucketPayer"
        value={query}
        className="w-full"
        suggestions={suggestions}
        completeMethod={searchPayer}
        dropdownAriaLabel="Add Payer"
        placeholder="Add payer"
        onChange={(e) => setQuery(e.value)}
        onSelect={(e) => {
          bucket.addPayer(e.value as IPayerOption);
          setQuery("");
        }}
      />
    </>
  );
}
